/**
 * Augmented Live Phase 2D — manager artefact streamer (ENG-6257).
 * Design: docs/design/here-now-s3-realtime.md ("Streaming during generation")
 *
 * Host-side counterpart to the codec (slice B), the draft channel (slice A), and
 * the stream route (slice C). The manager watches each agent's artefact output
 * and streams it to the private console preview *as it is written* — with no
 * agent tool call, which is what makes "link first" deterministic rather than
 * model-followed (an agent that finishes an artefact before posting a link
 * still streamed it live).
 *
 * **Poll-based, not fs.watch.** `fs.watch({recursive:true})` is unsupported on
 * Linux (the fleet), and the existing manager watchers (msteams pending-inbound)
 * already poll via readdir+stat on a cadence. So this mirrors that: a light
 * interval scans `~/.augmented/{codeName}/artifacts/<name>/index.html`, and any
 * file whose mtime changed is read and streamed at file-save granularity.
 *
 * Each artefact directory `<name>` maps to one draft. `<name>` is the stable
 * `client_token` handed to `POST /host/artifacts/draft`, so the draft (and its
 * channel id) survive manager restarts idempotently (ENG-6254). The per-artefact
 * `StreamEncoder` resets across restarts — harmless, since its first frame is a
 * keyframe the console preview resyncs from.
 *
 * Self-gating by construction: the scanner only does work for agents that
 * actually have artefacts under `~/.augmented/{codeName}/artifacts/`, so it's a
 * cheap no-op everywhere else. Everything is best-effort and non-throwing — a
 * streaming hiccup never disturbs the agent.
 *
 * The manager drives `scanOnce()` from its existing poll loop rather than
 * spinning a per-agent timer, so there is no new timer/leak surface in the
 * prod-critical supervisor; streaming cadence is the poll interval.
 */

import { join } from 'node:path';
import { homedir } from 'node:os';
import { readdir, stat, readFile } from 'node:fs/promises';
import { StreamEncoder } from '@augmented/core';

const ARTEFACT_ENTRY_FILE = 'index.html';

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Minimal API surface this module needs (the `api` client's `.post`). */
export interface ArtifactApi {
  post<T = unknown>(path: string, body: unknown): Promise<T>;
}

export interface ArtifactStreamDeps {
  api: ArtifactApi;
  log?: (msg: string) => void;
  /** Injectable clock for the encoder's keyframe cadence (testability). */
  now?: () => number;
}

/**
 * One artefact's streaming state: a lazily-created (idempotent) draft plus the
 * per-artefact encoder. `pushSnapshot` is called once per detected file change.
 */
export class ArtifactStreamSession {
  private readonly encoder: StreamEncoder;
  private draftId: string | null = null;
  private creating: Promise<string | null> | null = null;

  constructor(
    private readonly agentId: string,
    private readonly clientToken: string,
    private readonly deps: ArtifactStreamDeps,
    private readonly meta: { kind?: string; title?: string } = {},
  ) {
    this.encoder = new StreamEncoder(deps.now ? { now: deps.now } : {});
  }

  /** The draft id once created, else null. */
  get id(): string | null {
    return this.draftId;
  }

  /**
   * Create the draft on first use (idempotent via client_token — a manager
   * restart returns the same draft). Concurrent callers share one in-flight
   * request. Returns null on failure (best-effort; the next snapshot retries).
   */
  private async ensureDraft(): Promise<string | null> {
    if (this.draftId) return this.draftId;
    if (!this.creating) {
      this.creating = this.deps.api
        .post<{ id?: string }>('/host/artifacts/draft', {
          agent_id: this.agentId,
          client_token: this.clientToken,
          kind: this.meta.kind ?? 'other',
          ...(this.meta.title ? { title: this.meta.title } : {}),
        })
        .then((res) => {
          this.draftId = res?.id ?? null;
          return this.draftId;
        })
        .catch((err) => {
          this.deps.log?.(`[artifact-stream] draft create failed (${this.clientToken}): ${errMessage(err)}`);
          return null;
        })
        .finally(() => {
          this.creating = null;
        });
    }
    return this.creating;
  }

  /** Encode one snapshot and relay its frame(s). Best-effort, never throws. */
  async pushSnapshot(content: string): Promise<void> {
    const id = await this.ensureDraft();
    if (!id) return;
    const frames = this.encoder.encode(content);
    for (const frame of frames) {
      try {
        await this.deps.api.post(`/host/artifacts/${encodeURIComponent(id)}/stream`, {
          agent_id: this.agentId,
          frame,
        });
      } catch (err) {
        // A dropped frame is recoverable from the next keyframe — log, continue.
        this.deps.log?.(`[artifact-stream] frame ${frame.seq} failed (${id}): ${errMessage(err)}`);
      }
    }
  }
}

/** Filesystem surface the scanner needs (injectable for tests). */
export interface ArtifactFs {
  /** Names of the artefact directories under `artifactsDir` (empty if absent). */
  listArtefactNames(artifactsDir: string): Promise<string[]>;
  /** mtime in ms of `filePath`, or null if it doesn't exist / can't stat. */
  mtimeMs(filePath: string): Promise<number | null>;
  /** Read a file as UTF-8. */
  readFile(filePath: string): Promise<string>;
}

/**
 * Scans one agent's `artifacts/` directory on a cadence and streams any artefact
 * whose `index.html` changed since the last pass. One {@link ArtifactStreamSession}
 * per artefact directory.
 */
export class ArtifactStreamScanner {
  private readonly sessions = new Map<string, ArtifactStreamSession>();
  private readonly seenMtime = new Map<string, number>();
  private scanning = false;

  constructor(
    private readonly agentId: string,
    private readonly artifactsDir: string,
    private readonly fsDeps: ArtifactFs,
    private readonly deps: ArtifactStreamDeps,
  ) {}

  /** One scan pass. Reads + streams every changed artefact. Never throws. */
  async scanOnce(): Promise<void> {
    if (this.scanning) return; // don't overlap with a slow previous pass
    this.scanning = true;
    try {
      let names: string[];
      try {
        names = await this.fsDeps.listArtefactNames(this.artifactsDir);
      } catch {
        return; // dir absent / unreadable → nothing to stream this pass
      }
      for (const name of names) {
        const file = join(this.artifactsDir, name, ARTEFACT_ENTRY_FILE);
        const mtime = await this.fsDeps.mtimeMs(file).catch(() => null);
        if (mtime === null) continue;
        if (this.seenMtime.get(name) === mtime) continue; // unchanged
        let content: string;
        try {
          content = await this.fsDeps.readFile(file);
        } catch (err) {
          // Don't mark seen on a failed read (e.g. a transient write race) —
          // leave the mtime unrecorded so the next pass retries this snapshot.
          this.deps.log?.(`[artifact-stream] read failed (${name}): ${errMessage(err)}`);
          continue;
        }
        this.seenMtime.set(name, mtime);
        await this.sessionFor(name).pushSnapshot(content);
      }
      // Evict state for artefact dirs that disappeared, so a long-lived manager
      // doesn't accumulate sessions/encoders/mtimes unboundedly.
      const live = new Set(names);
      for (const key of this.seenMtime.keys()) if (!live.has(key)) this.seenMtime.delete(key);
      for (const key of this.sessions.keys()) if (!live.has(key)) this.sessions.delete(key);
    } finally {
      this.scanning = false;
    }
  }

  private sessionFor(name: string): ArtifactStreamSession {
    let session = this.sessions.get(name);
    if (!session) {
      session = new ArtifactStreamSession(this.agentId, name, this.deps, { title: name });
      this.sessions.set(name, session);
    }
    return session;
  }
}

// ── Production wiring ─────────────────────────────────────────────────────────

/** Agent artefact output directory: `~/.augmented/{codeName}/artifacts`. */
export function artifactsDirFor(codeName: string): string {
  return join(homedir(), '.augmented', codeName, 'artifacts');
}

/** Real-filesystem {@link ArtifactFs} backed by node:fs/promises. */
export const nodeArtifactFs: ArtifactFs = {
  async listArtefactNames(artifactsDir) {
    const entries = await readdir(artifactsDir, { withFileTypes: true });
    return entries.filter((e) => e.isDirectory() && !e.name.startsWith('.')).map((e) => e.name);
  },
  async mtimeMs(filePath) {
    try {
      return (await stat(filePath)).mtimeMs;
    } catch {
      return null;
    }
  },
  readFile(filePath) {
    return readFile(filePath, 'utf8');
  },
};
